import { buildLUTs } from 'src/redux/utils'

import {
  Survey,
  SurveyBuildingStockGoupConfig,
  SurveyCityTargetFieldGroupConfig,
  SurveyFuelBalanceGroupConfig,
  SurveyFuelParameterGroupConfig,
  SurveyLocationFieldGroupConfig,
  SurveyRenewableSourcesGroupConfig,
  SurveyUtilityFieldGroupConfig
} from './types'

const LocationSurveyGroupConfig: SurveyLocationFieldGroupConfig = {
  title: {
    stringConfig: {
      helperText: 'Change the project title',
      label: 'Title',
    },
  },
  city: {
    stringConfig: {
      helperText: 'Enter the city your model is from.',
      label: 'City',
    },
  },
  state: {
    stringConfig: {
      helperText: 'Enter the state your model is from.',
      label: 'State',
    },
  },
  country: {
    stringConfig: {
      helperText: 'Enter the country your model is from.',
      label: 'Country',
    },
  },
}

const UtilitySurveyGroupConfig: SurveyUtilityFieldGroupConfig = {
  utility_has_targets: {
    booleanConfig: {
      helperText: "Does your municipality's utility have targets?",
      label: "Does your municipality's utility have emissions targets?",
    },
  },
  utility_near_year: {
    numericConfig: {
      miniHeader: 'Near Goals',
      label: 'Year',
      helperText: 'Target year for near-term target',
      min: new Date().getFullYear(),
      max: new Date().getFullYear() + 55,
      step: 1,
      units: '',
      default: 2030,
    },
  },
  utility_near_percent: {
    numericConfig: {
      label: 'Target %',
      helperText: 'Near-term target % of original',
      min: 0,
      max: 100,
      step: 1,
      units: '',
      default: 20,
    },
  },
  utility_far_year: {
    numericConfig: {
      miniHeader: 'Far Goals',
      label: 'Year',
      helperText: 'Target year for long-term target',
      min: new Date().getFullYear(),
      max: new Date().getFullYear() + 55,
      step: 1,
      units: '',
      default: 2050,
    },
  },
  utility_far_percent: {
    numericConfig: {
      label: 'Target %',
      helperText: 'Far-term target % of original',
      min: 0,
      max: 100,
      step: 1,
      units: '',
      default: 5,
    },
  },
}

const CityTagetFieldGroupConfig: SurveyCityTargetFieldGroupConfig = {
  city_has_goals: {
    booleanConfig: {
      helperText: 'Does your municipality have emissions targets?',
      label: 'Does your municipality have emissions targets?',
    },
  },
  city_near_year: {
    numericConfig: {
      miniHeader: 'Near Goals',
      label: 'Year',
      helperText: 'Target year for near-term target',
      min: new Date().getFullYear(),
      max: new Date().getFullYear() + 55,
      step: 1,
      units: '',
      default: 2030,
    },
  },
  city_near_percent: {
    numericConfig: {
      label: 'Target %',
      helperText: 'Near-term target % of original',
      min: 0,
      max: 100,
      step: 1,
      units: '',
      default: 20,
    },
  },
  city_far_year: {
    numericConfig: {
      miniHeader: 'Far Goals',
      label: 'Year',
      helperText: 'Target year for long-term target',
      min: new Date().getFullYear(),
      max: new Date().getFullYear() + 55,
      step: 1,
      units: '',
      default: 2050,
    },
  },
  city_far_percent: {
    numericConfig: {
      label: 'Target %',
      helperText: 'Far-term target % of original',
      min: 0,
      max: 100,
      step: 1,
      units: '',
      default: 5,
    },
  },
}

const RenewableSourcesFieldGroupConfig: SurveyRenewableSourcesGroupConfig = {
  renewableSourcesOfInterest: {
    multiselectConfig: {
      label: 'Renewables',
      helperText: 'Select renewables to consider',
      options: [
        {
          value: 'none',
          label: 'None',
        },
        {
          value: 'solar',
          label: 'Solar Photovoltaic',
        },
        {
          value: 'wind',
          label: 'Wind',
        },
        {
          value: 'geo',
          label: 'Geothermal Power Plant',
        },
        {
          value: 'bio',
          label: 'Biomass',
        },
      ],
    },
  },
}

const BuildingStockGroupConfig: SurveyBuildingStockGoupConfig = {
  primaryBuildingStockComposition: {
    multiselectConfig: {
      label: 'Building Stock',
      helperText: 'Select primary building types',
      options: [
        {
          value: 'none',
          label: 'None',
        },
        {
          value: 'residential-sf',
          label: 'Residential (single-family)',
        },
        {
          value: 'residential-mf',
          label: 'Residential (multi-family)',
        },
        {
          value: 'residential-mr',
          label: 'Residential (mid-rise)',
        },
        {
          value: 'mixed-use-mr',
          label: 'Mixed Use (mid-rise)',
        },
        {
          value: 'mixed-use-hr',
          label: 'Mixed Use (high-rise)',
        },
        {
          value: 'commercial-mr',
          label: 'Commercial (mid-rise)',
        },
        {
          value: 'commercial-hr',
          label: 'Commercial (high-rise)',
        },
      ],
    },
  },
}

const FuelParameterGroupConfig: SurveyFuelParameterGroupConfig = {
  electricity_carbon: {
    numericConfig: {
      units: 'kWh / kg CO2eq',
      min: 0,
      max: 10,
      label: 'kWh / kg CO2eq',
      helperText: 'Electricity Carbon Coefficient',
      step: 0.001,
      miniHeader: 'Electricity',
      default: 0.5,
    },
  },
  electricity_cost: {
    numericConfig: {
      units: '$',
      min: 0,
      max: 20,
      label: 'Cost ($)',
      helperText: 'Electricity Cost',
      step: 0.01,
      default: 0.5,
    },
  },
  gas_carbon: {
    numericConfig: {
      units: 'kWh / kg CO2eq',
      min: 0,
      max: 10,
      label: 'kWh / kg CO2eq',
      helperText: 'Electricity Carbon Coefficient',
      step: 0.001,
      miniHeader: 'Gas',
      default: 0.5,
    },
  },
  gas_cost: {
    numericConfig: {
      units: '$',
      min: 0,
      max: 20,
      label: 'Cost ($)',
      helperText: 'Electricity Cost',
      step: 0.01,
      default: 0.5,
    },
  },
  oil_carbon: {
    numericConfig: {
      units: 'kWh / kg CO2eq',
      min: 0,
      max: 10,
      label: 'kWh / kg CO2eq',
      helperText: 'Electricity Carbon Coefficient',
      step: 0.001,
      miniHeader: 'Oil',
      default: 0.5,
    },
  },
  oil_cost: {
    numericConfig: {
      units: '$',
      min: 0,
      max: 20,
      label: 'Cost ($)',
      helperText: 'Electricity Cost',
      step: 0.01,
      default: 0.5,
    },
  },
}

const SurveyFuelBalanceFieldsConfig: SurveyFuelBalanceGroupConfig = {
  gas_balance: {
    numericConfig: {
      units: '%',
      min: 0,
      max: 100,
      label: '%',
      step: 1,
      default: 10,
      helperText: 'Natural Gas',
    },
  },
  oil_balance: {
    numericConfig: {
      units: '%',
      min: 0,
      max: 100,
      label: '%',
      step: 1,
      default: 10,
      helperText: 'Fuel or Heating Oil / Diesel / Kerosene',
    },
  },
  coal_balance: {
    numericConfig: {
      units: '%',
      min: 0,
      max: 100,
      label: '%',
      step: 1,
      default: 10,
      helperText: 'Coal',
    },
  },
  wood_balance: {
    numericConfig: {
      units: '%',
      min: 0,
      max: 100,
      label: '%',
      step: 1,
      default: 10,
      helperText: 'Wood',
    },
  },
  propane_balance: {
    numericConfig: {
      units: '%',
      min: 0,
      max: 100,
      label: '%',
      step: 1,
      default: 10,
      helperText: 'Propane / Bottled / Tank  Fuel',
    },
  },
  electricity_balance: {
    numericConfig: {
      units: '%',
      min: 0,
      max: 100,
      label: '%',
      step: 1,
      default: 10,
      helperText: 'Electricity',
    },
  },
  geothermal_balance: {
    numericConfig: {
      units: '%',
      min: 0,
      max: 100,
      label: '%',
      step: 1,
      default: 10,
      helperText: 'Gethermal (high or low temp)',
    },
  },
  solar_balance: {
    numericConfig: {
      units: '%',
      min: 0,
      max: 100,
      label: '%',
      step: 1,
      default: 10,
      helperText: 'Solar',
    },
  },
}

export const SurveyConfig: Survey = {
  location: {
    // Total should be moved to use selector and computed automatically
    total: 4,
    title: 'Location Data',
    description:
      'Enter geolocation metadata for your project.  This is just for reference; weather files will automatically be derived from your GIS file.',
    fields: LocationSurveyGroupConfig,
  },
  targets: {
    total: 5,
    title: 'Municipality Emissions Targets',
    description:
      'Enter greenhouse gas emissions targets for your municipality as percentages of a baseline value which will be defined in one of your simulation scenarios.',
    fields: CityTagetFieldGroupConfig,
  },
  utilityTargets: {
    total: 5,
    title: 'Utility Emissions Targets',
    description:
      'Enter greenhouse gas emissions targets for local utilities if known.  Currently, this data is not used in any numercal analysis, but may be useful for future reference.',
    fields: UtilitySurveyGroupConfig,
  },
  renewableSources: {
    total: 1,
    title: 'Renewables of Interest',
    description:
      'Select which renewables your municipality is considering as part of a green energy transition.  Currently, this data is not used in any numerical analysis directly, but will be useful to the energy modeler as they construct the UBEM.',
    fields: RenewableSourcesFieldGroupConfig,
  },
  stockMakeup: {
    total: 1,
    title: 'Building Stock Composition',
    description:
      'Select the primary composition of your building stock to focus on in the UBEM analysis.  These selections will not directly affect the numerical analysis, but the information will be useful for the energy modelr to have when building the UBEM.',
    fields: BuildingStockGroupConfig,
  },
  fuelSettings: {
    total: 6,
    title: 'Grid/Fuel Cost & Emissions',
    description:
      'Enter the cost and carbon coefficients for oil, gas, and electricity in your municipality.  These values wll be used in the numerical analyses and data visualizations, however you will have the ability to override them for specific scenarios (for instance, for future values).  If your municipality is in the US, the electricity cost will be updated automatically.',
    fields: FuelParameterGroupConfig,
  },
  fuelBalance: {
    total: 8,
    title: 'Heating Fuel Sources',
    description:
      'Select the approximate percentage of homes which use each fuel type as the primary source for heating.  Answer should sum to 100.   Currently, the data is not used in any numerical analysis, but will be useful for the energy modeler.',
    fields: SurveyFuelBalanceFieldsConfig,
  },
}

export const { entries: SurveyConfigEntries } = buildLUTs(SurveyConfig)
